import { FileUpload } from '@consigli/facade';
import { ExtendedFile, useProject, useProjectId, useServiceName } from '@consigli/hooks';
import {
  DPR_PROCESSOR_CATEGORIES,
  ProcessorPreferenceKey,
  ProjectConstructionType,
  ServiceName,
} from '@consigli/types';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';

import { ProcessorPreferences } from './processor-preferences';

interface AccordionSectionProps {
  title: string;
  children: React.ReactNode;
}

const AccordionSection = ({ title, children }: AccordionSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex cursor-pointer items-center gap-2 py-1 text-sm font-semibold text-day-neutral-subtle"
      >
        {isOpen ? <FaAngleDown /> : <FaAngleRight />}
        <span>{title}</span>
      </button>
      {isOpen && <div className="ml-5 mt-1">{children}</div>}
    </div>
  );
};

interface AdvancedControlsProps {
  selectedProcessors: ProcessorPreferenceKey[];
  setSelectedProcessors: (value: ProcessorPreferenceKey[]) => void;
  referenceDocs: ExtendedFile[];
  setReferenceDocs: Dispatch<SetStateAction<ExtendedFile[]>>;
  checkListFiles: ExtendedFile[];
  setCheckListFiles: Dispatch<SetStateAction<ExtendedFile[]>>;
  showMessagePanel: boolean;
  setShowMessagePanel: Dispatch<SetStateAction<boolean>>;
}

export const AdvancedControls = ({
  selectedProcessors,
  setSelectedProcessors,
  referenceDocs,
  setReferenceDocs,
  checkListFiles,
  setCheckListFiles,
  showMessagePanel,
  setShowMessagePanel,
}: AdvancedControlsProps) => {
  const { t } = useTranslation();
  const serviceName = useServiceName();
  const projectId = useProjectId();
  const { project } = useProject(projectId);

  const showAnalysisPreferences =
    serviceName === ServiceName.TENDER_DOCUMENTS ||
    serviceName === ServiceName.DESIGN_PACKAGE_REVIEW;

  const showReferenceDocs =
    serviceName === ServiceName.TENDER_DOCUMENTS &&
    project?.constructionTypes?.some(
      (type) =>
        type === ProjectConstructionType.ROAD ||
        type === ProjectConstructionType.OTHER_INFRASTRUCTURE,
    );

  const showCheckList =
    serviceName === ServiceName.MANAGEMENT_OPERATIONS_MAINTENANCE ||
    serviceName === ServiceName.TENDER_DOCUMENTS ||
    serviceName === ServiceName.DESIGN_PACKAGE_REVIEW;

  if (!showAnalysisPreferences && !showReferenceDocs && !showCheckList) {
    return null;
  }

  return (
    <div className="mt-6">
      <p className="mb-3 text-base font-semibold text-day-neutral-subtle">
        {t('advanced-controls.heading')}
      </p>
      <div className="flex flex-col gap-3">
        {showAnalysisPreferences && (
          <AccordionSection title={t('advanced-controls.analysis-preferences')}>
            <p className="mb-2 text-xs text-gray-500">
              {t('advanced-controls.analysis-preferences-description')}
            </p>
            <ProcessorPreferences
              selectedProcessors={selectedProcessors}
              setSelectedProcessors={setSelectedProcessors}
              {...(serviceName === ServiceName.DESIGN_PACKAGE_REVIEW && {
                categories: DPR_PROCESSOR_CATEGORIES,
                categoryLabelOverrides: { INSIGHT: t('dashboard.error-checker') },
              })}
            />
          </AccordionSection>
        )}

        {showCheckList && (
          <AccordionSection title={t('advanced-controls.checklist')}>
            <p className="mb-2 text-xs text-gray-500">
              {t('advanced-controls.checklist-description')}
            </p>
            <FileUpload
              files={checkListFiles}
              setFiles={setCheckListFiles}
              dropAreaLabel={t('document-upload.drag-n-drop-here')}
              dropAreaClassName="mt-2 h-48 w-full"
              fileButtonLabel={t('document-upload.browse-files')}
              folderButtonLabel={t('document-upload.browse-folders')}
              uploadListLabel={t('document-upload.files')}
              zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
              showMessagePanel={showMessagePanel}
              setShowMessagePanel={setShowMessagePanel}
            />
          </AccordionSection>
        )}

        {showReferenceDocs && (
          <AccordionSection title={t('advanced-controls.reference-material')}>
            <p className="mb-2 text-xs text-gray-500">
              {t('advanced-controls.reference-material-description')}
            </p>
            <FileUpload
              files={referenceDocs}
              setFiles={setReferenceDocs}
              dropAreaLabel={t('document-upload.drag-n-drop-here')}
              dropAreaClassName="mt-2 h-48 w-full"
              fileButtonLabel={t('document-upload.browse-files')}
              folderButtonLabel={t('document-upload.browse-folders')}
              uploadListLabel={t('document-upload.files')}
              zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
              showMessagePanel={showMessagePanel}
              setShowMessagePanel={setShowMessagePanel}
            />
          </AccordionSection>
        )}
      </div>
    </div>
  );
};
